import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Empty, Spin, Card, Row, Col, Statistic, Button, message } from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { getStudentCompetitions } from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';
import { tahsilatZamani, saatiYok } from '../../utils/tahsilatZamani';

const DURUM = {
  candidate:              { label: 'Aday',             color: 'default' },
  participation_approved: { label: 'Katılım Onaylı',   color: 'processing' },
  participation_declined: { label: 'Katılmıyor',       color: 'error' },
  fee_approved:           { label: 'Ücret Onaylı',     color: 'warning' },
  fee_declined:           { label: 'Ücretten Vazgeçti', color: 'error' },
  confirmed:              { label: 'Kesinleşti',       color: 'success' },
  cancelled:              { label: 'İptal',            color: 'error' },
};

/** Tutar + para birimi. Kur çevrilmiyor, birim her zaman yazılıyor. */
const tutar = (v, birim) =>
  `${Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${birim}`;

// Tahsilat saatinin ne zaman yazıldığı/yazılmadığı kuralı ORTAK yardımcıda —
// veli ekranı da aynısını kullanıyor, kopyalamıyoruz.

/**
 * Bir öğrencinin yarışma katılımları ve taksit borcu.
 *
 * İKİ YERDE KULLANILIYOR, tek bileşen:
 *   · Öğrenci detayı > "Yarışmalar" sekmesi — SALT OKUNUR
 *   · Yarışma Ödemeleri sayfası — tahsilat düğmeleriyle
 *
 * Fark `onTahsilat` prop'u: verilmezse hiçbir işlem düğmesi çizilmiyor.
 * İki ayrı bileşen yazılsaydı biri güncellenip öteki unutulurdu; bu
 * repoda aynı deseni iki yerde tutmak defalarca saptı.
 *
 * ⚠ KUR ÇEVRİLMİYOR. Özet kartları para birimi başına AYRI çiziliyor
 * (₺ ve € aynı yarışmada olabiliyor). Toplamları tek rakamda birleştiren
 * bir gösterim eklemeyin — kurum için de kural bu.
 */
const StudentCompetitionsPanel = ({ studentId, onTahsilat, yenileAnahtari }) => {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);

  const yukle = useCallback(async () => {
    if (!studentId) return;
    setYukleniyor(true);
    try {
      const { data } = await getStudentCompetitions(studentId);
      setVeri(data);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Yarışma bilgileri yüklenemedi.'));
      setVeri(null);
    } finally {
      setYukleniyor(false);
    }
  }, [studentId]);

  useEffect(() => { yukle(); }, [yukle, yenileAnahtari]);

  if (yukleniyor) return <div className="text-center p-8"><Spin /></div>;
  if (!veri) return null;

  const katilimlar = veri.participations || [];

  if (katilimlar.length === 0) {
    return <Empty description="Bu öğrencinin yarışma kaydı yok." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const birimler = Object.keys(veri.summary || {});

  const sutunlar = [
    { title: 'Taksit', dataIndex: 'no', width: 70, render: (v, r) => r.label || `${v}. taksit` },
    {
      title: 'Vade',
      dataIndex: 'due_date',
      width: 110,
      render: (v, r) => {
        if (!v) return <span className="text-gray-300">—</span>;
        const gecikti = r.remaining > 0 && dayjs(v).isBefore(dayjs(), 'day');
        return (
          <span className={gecikti ? 'text-red-500 font-medium' : undefined}>
            {dayjs(v).format('DD.MM.YYYY')}
          </span>
        );
      },
    },
    { title: 'Tutar', dataIndex: 'amount', align: 'right', render: (v, r) => tutar(v, r.currency) },
    {
      title: 'Tahsil',
      dataIndex: 'paid_amount',
      align: 'right',
      render: (v, r) => <span className="text-green-600">{tutar(v, r.currency)}</span>,
    },
    {
      title: 'Kalan',
      dataIndex: 'remaining',
      align: 'right',
      render: (v, r) => (v > 0
        ? <span className="text-red-500 font-bold">{tutar(v, r.currency)}</span>
        : <Tag color="success">Ödendi</Tag>),
    },
  ];

  // Tahsilat sutunu YALNIZCA onTahsilat verildiginde ekleniyor; ogrenci
  // detayinda sekme salt okunur olmali (kullanicinin karari).
  if (onTahsilat) {
    sutunlar.push({
      title: '',
      key: 'tahsil',
      width: 110,
      render: (_, r) => (r.remaining > 0 ? (
        <Button size="small" type="primary" icon={<WalletOutlined />} onClick={() => onTahsilat(r)}>
          Tahsilat
        </Button>
      ) : null),
    });
  }

  return (
    <div className="space-y-4">
      {birimler.length > 0 && (
        <Row gutter={12}>
          {birimler.map((b) => (
            <Col xs={24} md={8} key={b}>
              <Card size="small">
                <Statistic
                  title={`Kalan Borç (${b})`}
                  value={veri.summary[b].kalan}
                  precision={2}
                  suffix={b}
                  valueStyle={{ color: veri.summary[b].kalan > 0 ? '#cf1322' : '#3f8600' }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Toplam {tutar(veri.summary[b].toplam, b)} · Tahsil {tutar(veri.summary[b].odenen, b)}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {katilimlar.map((k) => (
        <Card
          key={k.participant_id}
          size="small"
          title={
            <span>
              {/* Yarismaya gidis: kullanici "yarismalar ve ogrenciler arasinda
                  gecisleri ayarlayalim" dedi. Ters yon (yarismadan ogrenciye)
                  katilimci adlari linklenerek zaten yapildi. */}
              <Link to={`/competitions/${k.competition_id}`}>{k.competition}</Link>
              <span className="text-gray-400"> · </span>
              <span>{k.stage}</span>
              {k.team && <Tag className="ml-2">{k.team}</Tag>}
            </span>
          }
          extra={<Tag color={DURUM[k.status]?.color}>{DURUM[k.status]?.label || k.status}</Tag>}
        >
          <div className="text-xs text-gray-500 mb-2">
            {k.location || '—'}
            {k.start_date && ` · ${dayjs(k.start_date).format('DD.MM.YYYY')}`}
            {k.attendee_count > 1 && ` · ${k.attendee_count} kişi`}
          </div>
          {k.installments.length === 0 ? (
            <div className="text-sm text-gray-400">Taksit tanımlanmamış.</div>
          ) : (
            <Table
              size="small"
              rowKey="id"
              dataSource={k.installments}
              columns={sutunlar}
              pagination={false}
              expandable={{
                /*
                  TAHSİLAT GEÇMİŞİ — "ne zaman, kim tahsil etti".

                  Eren Bey bildirdi (22.10.2026): veli arayıp sorduğunda
                  personel bu ekranda yalnızca "Ödendi" görüyordu ve soruya
                  cevap veremiyordu. Veri baştan beri saklanıyordu
                  (`received_by` + `paid_at`), yalnızca gösterilmiyordu.

                  Dil etap finans ekranıyla (`StageFinanceTab`) aynı tutuldu
                  — aynı bilgiyi iki ayrı biçimde göstermek kullanıcıyı
                  "hangisi doğru" diye düşündürür.

                  SİLME DÜĞMESİ BİLEREK YOK: bu bileşen öğrenci detayında
                  SALT OKUNUR çalışıyor. Yanlış girilen tahsilatı silmek
                  etap finans ekranında, yetkisi olan kişide.
                */
                rowExpandable: (r) => (r.payments || []).length > 0,
                expandedRowRender: (r) => (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={r.payments || []}
                    columns={[
                      {
                        title: 'Tarih',
                        dataIndex: 'paid_at',
                        width: 160,
                        render: (v, p) => (
                          <span>
                            {tahsilatZamani(v)}
                            {/* Saat yoksa kayıt zamanını göster: itiraz
                                durumunda "bu satır ne zaman girildi"
                                sorusunun cevabı bu. */}
                            {saatiYok(v) && p.recorded_at && (
                              <div className="text-xs text-gray-400">
                                girildi: {dayjs(p.recorded_at).format('DD.MM.YYYY HH:mm')}
                              </div>
                            )}
                          </span>
                        ),
                      },
                      {
                        title: 'Tutar',
                        dataIndex: 'amount',
                        width: 120,
                        align: 'right',
                        render: (v) => tutar(v, r.currency),
                      },
                      { title: 'Yöntem', dataIndex: 'payment_method', width: 110, render: (v) => v || '—' },
                      {
                        title: 'Tahsil Eden',
                        key: 'receiver',
                        render: (_, p) => (p.receiver
                          ? `${p.receiver.name} ${p.receiver.surname}`
                          // Modül öncesinden taşınan kayıtlarda boş olabilir
                          // (migration eski kolondan doldurdu, o da boştu).
                          : <span className="text-gray-300">—</span>),
                      },
                      { title: 'Not', dataIndex: 'notes', render: (v) => v || '—' },
                    ]}
                  />
                ),
              }}
            />
          )}
        </Card>
      ))}
    </div>
  );
};

export default StudentCompetitionsPanel;
